export const WATCHLIST_LIMITS = {
  MAX_INSTRUMENTS: 24,
  WARN_THRESHOLD: 22,
  MATURITY_SEEDING: 100,   // candles before engine outputs shown
  MATURITY_WARMING: 200,   // candles before auto-trade eligible
} as const;

export const CADENCE = {
  WATCHLIST_PRICE_MS: 60_000,
  WATCHLIST_SPARKLINE_MS: 300_000,
  COCKPIT_HEADER_PRICE_MS: 30_000,
  COCKPIT_CHART_MS: 300_000,
  COCKPIT_METRICS_MS: 300_000,
  DECISION_CARD_MS: 300_000,
  AUDIT_PNL_MS: 300_000,
  CRON_POLL_MS: 60_000,
} as const;

export const CANDLE_WINDOW_SIZE = 200;

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 25,
  MAX_PAGE_SIZE: 100,
} as const;

// --- Engine v2 Constants (decision-engine-spec.md Section 8) ---

export const ENGINE_CONSTANTS = {
  // Candle window
  CANDLE_WINDOW_SIZE: 200,

  // Normalisation clamp ranges
  ADX_CLAMP_MAX: 60,
  ATR_RATIO_CLAMP_MAX: 3,
  VOL_RATIO_CLAMP_MAX: 5,
  SMA_DIST_CLAMP_MAX: 5,
  DRAWDOWN_CLAMP_MAX: 20,

  // Regime confirmation
  REGIME_CONFIRMATION_CANDLES: 5,
  REGIME_VOLATILE_CONFIRMATION: 1,

  // Risk score smoothing
  RISK_SCORE_ALPHA: 0.3,
  RISK_SCORE_DEAD_ZONE: 3,

  // Risk band hysteresis thresholds
  RISK_BAND_LOW_EXIT: 32,
  RISK_BAND_MODERATE_ENTRY: 32,
  RISK_BAND_ELEVATED_ENTRY: 58,
  RISK_BAND_ELEVATED_EXIT: 52,
  RISK_BAND_HIGH_ENTRY: 78,
  RISK_BAND_HIGH_EXIT: 72,
  RISK_BAND_LOW_MAX: 28,

  // Material change thresholds
  VOLUME_SPIKE_RATIO: 2.0,
  ATR_SPIKE_PCT: 0.30,
  ATR_SPIKE_LOOKBACK: 6,
  EMA_CROSSOVER_CONFIRMATION: 1,

  // Cooldown and staleness
  COOLDOWN_CANDLES: 1,
  STALENESS_CANDLES: 6,
  CARD_EXPIRY_CANDLES: 2,

  // Position sizing
  DEFAULT_ACCOUNT_BALANCE: 10000,
  DEFAULT_RISK_PERCENT: 1.0,
  ELEVATED_RISK_SIZE_MULTIPLIER: 0.5,

  // ATR history buffer
  ATR_HISTORY_LENGTH: 20,

  // Volatile regime extra conditions
  VOLATILE_ATR_PCT_THRESHOLD: 0.015,
  VOLATILE_ATR_RATIO_THRESHOLD: 2.0,

  // Ranging minimum ATR
  RANGING_MIN_ATR_PCT: 0.001,
} as const;

// Weight profile: no position (market risk assessment)
export const ENGINE_WEIGHTS_NO_POSITION = {
  rsiExtremity: 0.20,
  trendWeakness: 0.20,
  volatilityStress: 0.20,
  spreadCost: 0.10,
  priceExtension: 0.15,
  volumeDivergence: 0.15,
  drawdownStress: 0.00,
  stopProximity: 0.00,
} as const;

// Weight profile: with position (exposure risk assessment)
export const ENGINE_WEIGHTS_WITH_POSITION = {
  rsiExtremity: 0.10,
  trendWeakness: 0.10,
  volatilityStress: 0.15,
  spreadCost: 0.05,
  priceExtension: 0.10,
  volumeDivergence: 0.10,
  drawdownStress: 0.25,
  stopProximity: 0.15,
} as const;

// Threshold definitions for hysteresis (Section 2.1)
export const THRESHOLD_DEFS = {
  isTrending:         { entry: 0.25,  exit: 0.167, field: 'adxNorm' as const, direction: 'above' as const },
  isDead:             { entry: 0.10,  exit: 0.133, field: 'adxNorm' as const, direction: 'below' as const },
  isOverbought:       { entry: 0.70,  exit: 0.65,  field: 'rsiNorm' as const, direction: 'above' as const },
  isOversold:         { entry: 0.30,  exit: 0.35,  field: 'rsiNorm' as const, direction: 'below' as const },
  isVolatile:         { entry: 0.50,  exit: 0.433, field: 'atrRatioNorm' as const, direction: 'above' as const },
  isCompressed:       { entry: 0.233, exit: 0.267, field: 'atrRatioNorm' as const, direction: 'below' as const },
  isVolumeSpike:      { entry: 0.50,  exit: 0.40,  field: 'volRatioNorm' as const, direction: 'above' as const },
  isSpreadWide:       { entry: 0.50,  exit: 0.40,  field: 'spreadNorm' as const, direction: 'above' as const },
  isBullishMomentum:  { entry: 0.5,   exit: 0.2,   field: 'emaDist' as const, direction: 'above' as const },
  isBearishMomentum:  { entry: -0.5,  exit: -0.2,  field: 'emaDist' as const, direction: 'below' as const },
  isMaSeparatedBull:  { entry: 1.0,   exit: 0.5,   field: 'smaGap' as const, direction: 'above' as const },
  isMaSeparatedBear:  { entry: -1.0,  exit: -0.5,  field: 'smaGap' as const, direction: 'below' as const },
} as const;

// Material change trigger priority (Section 5.5)
export const TRIGGER_PRIORITY: readonly string[] = [
  'stop_hit',
  'regime_change',
  'risk_band_change',
  'tp1_hit',
  'tp2_hit',
  'atr_spike',
  'insider_cluster_buy',
  'insider_large_purchase',
  'insider_heavy_selling',
  'volume_spike',
  'ema_crossover',
  'entry_zone',
  'staleness',
] as const;

// --- Opportunity Score Constants ---

export const OPPORTUNITY_WEIGHTS = {
  trend_clarity: 0.30,
  liquidity_quality: 0.22,
  noise_suitability: 0.18,
  extension_score: 0.18,
  insider_signal: 0.12,
} as const;

export const OPPORTUNITY_CONSTANTS = {
  // Noise suitability bell curve: sweet spot is ATR% between 0.3% and 1.5%
  NOISE_SWEET_LOW: 0.003,
  NOISE_SWEET_HIGH: 0.015,
  NOISE_FALLOFF_MAX: 0.03,   // ATR% >= 3% scores 0

  // Extension penalty: distance from SMA20 in ATR multiples
  EXTENSION_MAX_ATR: 3,      // >= 3 ATR from SMA20 scores 0

  // Smoothing (same EMA approach as risk score)
  SMOOTHING_ALPHA: 0.3,
  DEAD_ZONE: 3,
} as const;

// --- Insider Signal Constants ---

export const INSIDER_CONSTANTS = {
  SIGNAL_DECAY_DAYS: 30,          // Linear decay over 30 calendar days
  LARGE_PURCHASE_THRESHOLD: 100_000,  // $100K minimum for large purchase
  OWNERSHIP_INCREASE_THRESHOLD: 0.10, // 10% ownership increase
  CLUSTER_WINDOW_DAYS: 7,         // Days to look for cluster buys
  CLUSTER_MIN_INSIDERS: 2,        // Minimum insiders for cluster
  CLEANUP_TRANSACTIONS_DAYS: 90,  // Delete transactions older than 90 days
  CLEANUP_CLUSTERS_DAYS: 365,     // Delete clusters older than 1 year
  EDGAR_REQUEST_DELAY_MS: 150,    // Delay between EDGAR requests
  EDGAR_MAX_INPUT_BYTES: 512 * 1024, // 512KB max XML input
  EDGAR_MAX_FAILURES: 5,          // Circuit breaker threshold
} as const;

// --- Scalper Constants ---

export const SCALPER_CONSTANTS = {
  STOP_ATR_MULT: 1.0,        // Stop = 1 ATR (was 2 ATR)
  TP1_ATR_MULT: 1.0,         // TP1 = 1 ATR (was 3 ATR)
  TP2_ATR_MULT: 2.0,         // TP2 = 2 ATR (was 5 ATR)
  QUICK_PROFIT_PCT: 0.003,   // Close at +0.3% if held >2 candles
  QUICK_PROFIT_CANDLES: 2,   // Min candles before quick-profit exit
  RSI_BUY_MAX: 65,           // Buy if RSI < 65 (relaxed from 55)
  RSI_SELL_MAX: 35,          // Sell/avoid if RSI > 35
  COOLDOWN_CANDLES: 1,       // 1 candle cooldown between trades (was 3)
  CARD_EXPIRY_CANDLES: 2,    // Cards expire after 2 candles (was 8)
  TRAILING_BREAKEVEN_PCT: 0.002, // Move stop to breakeven at +0.2% (was 2%)
  TRAILING_START_PCT: 0.004,     // Start trailing at +0.4% (was 5%)
  TRAILING_DISTANCE_PCT: 0.002,  // Trail distance 0.2% (was 2%)
  DEAD_PRUNE_MS: 4 * 60 * 60 * 1000,  // Remove after 4 hours in dead regime
} as const;

// --- Auto-Trade Defaults ---

export const AUTO_TRADE_DEFAULTS = {
  MAX_POSITION_SIZE: 10,
  RISK_PERCENT: 1.0,
} as const;

// --- Safety Exit (hard backstop) ---
//
// A 5-minute scalper must never carry a position for days. These thresholds
// drive `runSafetyExit()`, which force-closes any open position that has bled
// past MAX_LOSS_PCT or been held longer than MAX_HOLD_CANDLES — regardless of
// whether a protective stop is still tracked. This is the catch-all that
// guarantees no position can bleed for weeks again, including legacy positions
// that lost their protective tracking.
export const SAFETY_EXIT = {
  // Force-close once a position is down this % from entry (positive number).
  // This is the HARD backstop of last resort — the graduated soft stop below
  // should almost always fire first.
  MAX_LOSS_PCT: 8,
  // Graduated soft stop: cut a loser at this % below entry (positive number),
  // recorded as `stop_exit`, BEFORE it can bleed to the MAX_LOSS_PCT backstop.
  // This is the worker-side stop that reliably fires every cycle on the fresh
  // positions snapshot — it does NOT depend on a broker-side stop order filling
  // or on `protective:` KV tracking surviving. Halving the average loss here is
  // the single biggest lever on the win/loss payoff ratio.
  SOFT_STOP_PCT: 3.5,
  // Trailing high-water stop for WINNERS. Once a position's peak gain since open
  // reaches TRAIL_ARM_PCT, a trailing stop arms; the position is then allowed to
  // keep running and only exits (as `stop_exit`) if it gives back
  // TRAIL_GIVEBACK_PCT from its high-water price. This lets winners trend instead
  // of being blindly time-cut at a small gain by MAX_HOLD_CANDLES.
  TRAIL_ARM_PCT: 2.0,
  TRAIL_GIVEBACK_PCT: 1.5,
  // Force-close once a position has been held this many 5-min candles.
  // ~78 candles ≈ one US trading session (6.5h) → "one trading day" default.
  // NOTE: an armed winner (peak gain ≥ TRAIL_ARM_PCT) is exempt from this time
  // cut — it is managed by the trailing stop instead, so a good trend is not
  // closed just because the clock ran out.
  MAX_HOLD_CANDLES: 78,
  // 5-min candle duration in ms — used to turn hold age into a candle count.
  CANDLE_MS: 5 * 60 * 1000,
  // KV key prefix recording when a position was first observed open.
  OPENED_AT_PREFIX: 'position_opened:',
  // KV key prefix recording the highest price seen since a position opened
  // (the high-water mark used by the trailing stop above).
  HIGH_WATER_PREFIX: 'position_high:',
  // TTL (s) for the opened-at / high-water markers — long enough that a stuck
  // position keeps accurate state, short enough to self-clean (30 days).
  OPENED_AT_TTL_S: 30 * 24 * 60 * 60,
} as const;

// --- Strategist Constants ---

export const STRATEGIST_CONSTANTS = {
  RATE_LIMIT_MS: 300_000,     // Max 1 call per instrument per 5 minutes
  CACHE_TTL_S: 3600,          // Cache strategist output for 1 hour
  MAX_BULLETS: 3,
  MAX_PLAN_STEPS: 4,
  MAX_INVALIDATE: 3,
  MAX_BULLET_LENGTH: 140,
  MAX_TONE_LENGTH: 140,
} as const;

// --- LLM Model Routing Constants ---

export type LLMTaskType = 'strategist' | 'recommendations' | 'explain';

export const LLM_CONSTANTS = {
  PROMPT_VERSION: 'v2',
  ROUTINE_MODEL: 'claude-haiku-4-5-20251001',
  PREMIUM_MODEL: 'claude-sonnet-4-6',
  ROUTINE_MAX_TOKENS: 512,
  PREMIUM_MAX_TOKENS: 1536,
  PREMIUM_RATE_LIMIT_MS: 900_000,  // 15 min per instrument
  CACHE_TTL_S: 3600,               // 1 hour hash-based cache
} as const;

// --- Watchlist Recommendation Constants ---

export const RECOMMENDATION_CONSTANTS = {
  MAX_SUGGESTIONS: 10,
  CACHE_TTL_S: 86400,          // 24 hours
  RATE_LIMIT_MS: 300_000,      // 5 minutes between calls per user
} as const;

